'use client'

import { Fragment, useState } from 'react'

import { Star } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Link } from '@/i18n/navigation'

import type { ReviewsProps } from './schema'

/** How many cards a reader sees before asking for more. Every card is in the HTML, hidden
 *  rather than omitted, so a crawler reads them all. */
const INITIALLY_VISIBLE = 5

type Cards = NonNullable<ReviewsProps['cards']>

/** One star of a rating, gold where it counts and light grey where it does not. */
export function RatingStar({
    filled,
    className,
}: {
    readonly filled: boolean
    readonly className?: string
}) {
    return (
        <Star
            aria-hidden="true"
            className={cn(
                'h-5 w-5 shrink-0',
                filled ? 'fill-[#e9c530] text-[#e9c530]' : 'fill-[#e2e2e2] text-[#e2e2e2]',
                className,
            )}
        />
    )
}

/** A rating drawn as five stars, the figure itself for assistive technology. */
export function Stars({ rating, name }: { readonly rating: number; readonly name: string }) {
    return (
        <span className="mr-2 flex" role="img" aria-label={name}>
            {Array.from({ length: 5 }, (_, index) => (
                <RatingStar key={index} filled={index < Math.round(rating)} />
            ))}
        </span>
    )
}

/** The written reviews, the first few shown and the rest behind a toggle where the copy
 *  gives it words. */
export function ReviewCards({ items, labels, showMore, showLess }: Cards) {
    const [expanded, setExpanded] = useState(false)
    const canExpand = items.length > INITIALLY_VISIBLE && showMore !== undefined
    const visible = canExpand && !expanded ? INITIALLY_VISIBLE : items.length

    return (
        <div>
            <ul>
                {items.map((card, index) => {
                    const remarks = [
                        { label: labels.pros, text: card.pros },
                        { label: labels.cons, text: card.cons },
                        { label: labels.tips, text: card.tips },
                    ].filter((remark) => remark.text !== undefined)
                    return (
                        <li
                            key={index}
                            hidden={index >= visible}
                            className={cn(
                                'border-x border-t border-[#d9dee5] px-4 pt-4 pb-6 text-base text-text-secondary md:p-6',
                                index === 0 && 'rounded-t-[7px]',
                                index === visible - 1 && 'rounded-b-[7px] border-b',
                            )}
                        >
                            {card.stars !== undefined && (
                                <div className="flex items-center pb-4">
                                    <Stars rating={card.stars} name={card.rating ?? ''} />
                                    <span aria-hidden="true">{card.rating}</span>
                                </div>
                            )}
                            <div className="flex flex-col md:flex-row">
                                {card.reviewer && (
                                    <span className="font-bold">{card.reviewer}</span>
                                )}
                                {card.date && <span className="md:mx-2">{card.date}</span>}
                                {card.route.href ? (
                                    <Link
                                        className="text-primary hover:underline"
                                        href={card.route.href}
                                    >
                                        {card.route.label}
                                    </Link>
                                ) : (
                                    <span>{card.route.label}</span>
                                )}
                            </div>
                            {remarks.length > 0 && (
                                <p className="mt-6">
                                    {remarks.map((remark, at) => (
                                        <Fragment key={at}>
                                            {at > 0 && ' '}
                                            {remark.label && (
                                                <>
                                                    <strong className="font-bold">
                                                        {remark.label.text}
                                                    </strong>{' '}
                                                </>
                                            )}
                                            {remark.text}
                                        </Fragment>
                                    ))}
                                </p>
                            )}
                        </li>
                    )
                })}
            </ul>
            {canExpand && (
                <button
                    type="button"
                    className="mt-8 rounded-[7px] border border-strong bg-strong px-5 py-[15px] text-sm leading-5 font-bold tracking-[2px] text-strong-foreground uppercase hover:border-strong-hover hover:bg-strong-hover"
                    aria-expanded={expanded}
                    onClick={() => setExpanded((open) => !open)}
                >
                    {expanded && showLess ? showLess.text : showMore.text}
                </button>
            )}
        </div>
    )
}
